import { Head, useForm } from "@inertiajs/react";
import { Download, Upload } from "lucide-react";
import SchoolLayout from "@/components/schools/SchoolLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import StudentNameGuidance from "@/components/schools/StudentNameGuidance";
import ImportReport, { CsvFileInput } from "@/components/schools/ImportReport";
import { GuideTip } from "@/components/schools/setup/SetupPrimitives";

export default function ClassroomsImport({ school, membership, campaign, template_path, import_path, back, report }) {
  // The controller reads the plain `file` param (see ClassroomImportsController#create).
  const { setData, post, processing, progress } = useForm({ file: null });

  const submit = (e) => {
    e.preventDefault();
    post(import_path, { forceFormData: true });
  };

  return (
    <SchoolLayout school={school} membership={membership} eyebrow={campaign.name} title="Import students" back={back}>
      <Head title={`${campaign.name} · Import students`} />

      <div className="space-y-6">
        <ImportReport report={report} />

        <Card>
          <CardHeader>
            <CardTitle>Upload a class list</CardTitle>
            <CardDescription>
              One row per student. Classrooms are created from the classroom column, and every student gets an activation code.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <section className="space-y-3">
              <h3 className="text-sm font-semibold text-foreground">1. Start with the template</h3>
              <p className="text-sm leading-relaxed text-muted-foreground">
                Four columns: first name, last name, classroom and grade. Keep the column names exactly as they are.
              </p>
              <StudentNameGuidance id="import-student-name-guidance" />
              <Button variant="outline" asChild>
                <a href={template_path} download>
                  <Download className="h-4 w-4" />
                  Download the template
                </a>
              </Button>
            </section>

            <form onSubmit={submit} className="space-y-5 border-t border-border pt-6" encType="multipart/form-data">
              <h3 className="text-sm font-semibold text-foreground">2. Upload your filled-in file</h3>
              <CsvFileInput
                label="Class list (CSV)"
                onChange={(file) => setData("file", file)}
                describedBy="import-student-name-guidance"
                columns="first_name, last_name, classroom, grade"
              />
              {campaign.student_limit && (
                <p className="text-xs text-muted-foreground">
                  Your {campaign.plan_name} plan covers up to {campaign.student_limit.toLocaleString()} students.
                  This campaign has {campaign.student_count.toLocaleString()}.
                </p>
              )}
              {progress && <p className="text-sm text-muted-foreground">Uploading… {progress.percentage}%</p>}
              <Button type="submit" disabled={processing}>
                <Upload className="h-4 w-4" />
                {processing ? "Importing…" : "Import students"}
              </Button>
            </form>
          </CardContent>
        </Card>

        <GuideTip title="Good to know">
          <ul className="list-disc space-y-1.5 pl-5">
            <li><strong className="font-medium text-foreground">Classroom:</strong> the name families know, like “Room 12”, “Mrs. Patel's class” or “5A”.</li>
            <li><strong className="font-medium text-foreground">Grade:</strong> however your school writes it, like “Grade 5” or “Kindergarten”.</li>
            <li>Two students with the same full name need two rows. They become “Tom L.” and “Tom L-2.”</li>
            <li>Uploading again is safe: nobody is added twice, and nobody is removed. To add another child who shares a classmate's full name, include every student with that name in the file, or use Add student.</li>
            <li>If any row has a problem, nothing is saved. Fix the file and upload it again.</li>
          </ul>
        </GuideTip>
      </div>
    </SchoolLayout>
  );
}
